import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './Login';
import Domains from './Domains';
import DomainDetail from './DomainDetail';
import Images from './Images';
import ImageDetail from './ImageDetail';
import DomainRedirects from './DomainRedirects';
import DomainRedirectEditor from './DomainRedirectEditor';
import DomainDirectory from './directory/DomainDirectory';
import DirectoryEditor from './directory/DirectoryEditor';
import Search from './Search';
import Bookmarks from './Bookmarks';
import Emails from './Emails';
import PageEditor from './PageEditor';
import DomainPages from './DomainPages';
import DomainProducts from './DomainProducts';
import DomainOffers from './DomainOffers';
import DomainRankings from './rankings/DomainRankings';
import RankingsFleet from './rankings/RankingsFleet';
import Header from './Header';
import Users from './Users';
import McpReference from './McpReference';
import ApiKeys from './ApiKeys';
import Settings from './Settings';
import { DomainProvider, useDomain } from './DomainContext';
import { ToastProvider } from './Toast';
import Spinner from './ui/Spinner';

// Syncs the selected domain when navigating to /domain/:id routes
const DomainRouteSync = () => {
  const location = useLocation();
  const { selectDomainById, domainsLoaded } = useDomain();

  useEffect(() => {
    if (!domainsLoaded) return;
    const match = location.pathname.match(/^\/domain\/([^/]+)/);
    if (match) {
      selectDomainById(decodeURIComponent(match[1]));
    }
  }, [location.pathname, domainsLoaded, selectDomainById]);

  return null;
};


const App = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if token exists in localStorage on app load
    if (localStorage.getItem('access_token')) {
      setAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  // Show loading while checking authentication
  if (isLoading) {
    return <Spinner label="Loading…" />;
  }

  const guard = (element) => (authenticated ? element : <Navigate to="/login" replace />);

  return (
    <Router>
      <DomainProvider>
        <ToastProvider>
          <DomainRouteSync />
          <div style={{ fontFamily: 'Arial, sans-serif' }}>
            <Header authenticated={authenticated} />
            <div className="main-container" style={{ padding: '20px' }}>
              <Routes>
                <Route path="/login" element={<Login setAuthenticated={setAuthenticated} />} />
                <Route path="/mcp" element={guard(<McpReference />)} />
                <Route path="/" element={guard(<Domains view="home" />)} />
                <Route path="/domains" element={guard(<Domains view="sites" />)} />
                <Route path="/domain/:domainId" element={guard(<DomainDetail />)} />
                <Route path="/domain/:domainId/settings" element={guard(<DomainDetail view="settings" />)} />
                <Route path="/images" element={guard(<Images />)} />
                <Route path="/images/:mediaId" element={guard(<ImageDetail />)} />
                <Route path="/search" element={guard(<Search />)} />
                <Route path="/bookmarks" element={guard(<Bookmarks />)} />
                <Route path="/emails" element={guard(<Emails />)} />
                <Route path="/domain/:domainId/pages" element={guard(<DomainPages />)} />
                <Route path="/domain/:domainId/pages/:pageId/edit" element={guard(<PageEditor />)} />
                <Route path="/domain/:domainId/redirects" element={guard(<DomainRedirects />)} />
                <Route path="/domain/:domainId/products" element={guard(<DomainProducts />)} />
                <Route path="/domain/:domainId/offers" element={guard(<DomainOffers />)} />
                <Route path="/domain/:domainId/rankings" element={guard(<DomainRankings />)} />
                <Route path="/rankings" element={guard(<RankingsFleet />)} />
                <Route path="/domain/:domainId/redirects/:redirectId/edit" element={guard(<DomainRedirectEditor />)} />
                <Route path="/domain/:domainId/directory" element={guard(<DomainDirectory />)} />
                <Route path="/domain/:domainId/directory/:collection/:slug/edit" element={guard(<DirectoryEditor />)} />
                <Route path="/users" element={guard(<Users />)} />
                <Route path="/api-keys" element={guard(<ApiKeys />)} />
                <Route path="/settings" element={guard(<Settings setAuthenticated={setAuthenticated} />)} />
                <Route path="*" element={<Navigate to={authenticated ? "/" : "/login"} replace />} />
              </Routes>
            </div>
          </div>
        </ToastProvider>
      </DomainProvider>
    </Router>
  );
};

export default App;
